import { memo, useCallback, useState } from 'react';

import { Repeat2Icon } from '@air/design-icons';

import { browserTimeZone } from '~/components/Automations/utils/automationSchedule';
import { WorkspaceAgentPanelMyTasksRowFrame } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowFrame';
import { WorkspaceAgentPanelMyTasksRowMetaContainer } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowMeta';
import { WORKSPACE_AGENT_AUTOMATION_ROW_ID_ATTRIBUTE } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentAutomationRowFocusAttribute';
import { type PrivateWorkspaceAgentPanelAutomationLatestRun } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentPanelAutomationLatestRuns';
import { WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_AUTOMATION } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentSessionVirtualRows';
import {
  formatWorkspaceAgentAutomationDisplayName,
  formatWorkspaceAgentAutomationLastRunLabel,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentPanelAutomationDisplay';
import { type Automation } from '~/hooks/queries/automations/usePrivateAutomationsList';

export type PrivateWorkspaceAgentPanelMyTasksAutomationRowProps = {
  automation: Automation;
  latestRun: PrivateWorkspaceAgentPanelAutomationLatestRun;
  onOpen: (automationId: string) => void;
};

export const PrivateWorkspaceAgentPanelMyTasksAutomationRow = memo(
  ({ automation, latestRun, onOpen }: PrivateWorkspaceAgentPanelMyTasksAutomationRowProps) => {
    const [viewerTimeZone] = useState(browserTimeZone);
    const [now] = useState(() => new Date());

    const title = formatWorkspaceAgentAutomationDisplayName(automation);
    const lastRunLabel = formatWorkspaceAgentAutomationLastRunLabel({
      lastRunAt: automation.lastRunAt ?? latestRun?.startedAt,
      now,
      timeZone: viewerTimeZone,
    });

    const handleOpen = useCallback(() => {
      onOpen(automation.id);
    }, [automation.id, onOpen]);

    return (
      <WorkspaceAgentPanelMyTasksRowFrame
        {...{ [WORKSPACE_AGENT_AUTOMATION_ROW_ID_ATTRIBUTE]: automation.id }}
        data-testid={WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_AUTOMATION}
        density="compact"
        icon={
          <span className="relative size-5 shrink-0 overflow-visible">
            <span className="flex size-5 items-center justify-center ads:text-icon-subtle text-gray-9">
              <Repeat2Icon aria-hidden size={16} />
            </span>
          </span>
        }
        meta={
          <WorkspaceAgentPanelMyTasksRowMetaContainer>
            <span className="shrink-0 tabular-nums">{lastRunLabel}</span>
          </WorkspaceAgentPanelMyTasksRowMetaContainer>
        }
        onClick={handleOpen}
        overflowMenu={null}
        preview={null}
        title={title}
      />
    );
  },
);

PrivateWorkspaceAgentPanelMyTasksAutomationRow.displayName = 'PrivateWorkspaceAgentPanelMyTasksAutomationRow';
